import {
  Button,
  Combobox,
  DateRangePicker,
  FormField,
  TextInput,
} from "@a2z/ui";

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { intlLocale } from "@/lib/formatting/locale";
import type {
  IntegrationLogDirection,
  IntegrationLogStatus,
} from "@/lib/api/types";
import type { IntegrationLogFiltersProps } from "../types/integration-logs.types";
import {
  buildIntegrationLogQuery,
  countActiveIntegrationLogFilters,
} from "../services/integration-log-search-params";

const STATUS_VALUES: IntegrationLogStatus[] = ["success", "failed", "pending"];
const DIRECTION_VALUES: IntegrationLogDirection[] = ["outbound", "inbound"];

const STATUS_LABEL_KEY: Record<IntegrationLogStatus, string> = {
  success: keys.integrationLogs.statusSuccess,
  failed: keys.integrationLogs.statusFailed,
  pending: keys.integrationLogs.statusPending,
};

const DIRECTION_LABEL_KEY: Record<IntegrationLogDirection, string> = {
  outbound: keys.integrationLogs.directionOutbound,
  inbound: keys.integrationLogs.directionInbound,
};

function isStatus(value: string): value is IntegrationLogStatus {
  return (STATUS_VALUES as string[]).includes(value);
}

function isDirection(value: string): value is IntegrationLogDirection {
  return (DIRECTION_VALUES as string[]).includes(value);
}

const APPLY_DEBOUNCE_MS = 300;

function normalize(value: string | undefined): string {
  return value?.trim() ?? "";
}

/**
 * Inline filter bar. Every control sits directly above the table — a support
 * agent chasing one order shouldn't have to open a dialog to narrow the list,
 * and the applied filters stay visible while reading results.
 */
export function IntegrationLogFilters({
  basePath,
  limit,
  locale,
  initialFilters,
  storeOptions,
}: IntegrationLogFiltersProps) {
  const navigate = useNavigate();
  const t = useTranslations();
  // Future days are disabled — a log row can never be in the future.
  const todayIso = new Date().toISOString().slice(0, 10);

  const [search, setSearch] = useState(normalize(initialFilters.search));
  const [storeId, setStoreId] = useState(normalize(initialFilters.storeId));
  const [status, setStatus] = useState(normalize(initialFilters.status));
  const [direction, setDirection] = useState(
    normalize(initialFilters.direction),
  );
  const [provider, setProvider] = useState(normalize(initialFilters.provider));
  const [dateFrom, setDateFrom] = useState(normalize(initialFilters.dateFrom));
  const [dateTo, setDateTo] = useState(normalize(initialFilters.dateTo));

  const activeFilterCount = countActiveIntegrationLogFilters(initialFilters);

  const nextSearch = normalize(search);
  const nextStoreId = normalize(storeId);
  const nextStatus = normalize(status);
  const nextDirection = normalize(direction);
  const nextProvider = normalize(provider);
  const nextDateFrom = normalize(dateFrom);
  const nextDateTo = normalize(dateTo);

  const hasPendingChanges =
    nextSearch !== normalize(initialFilters.search) ||
    nextStoreId !== normalize(initialFilters.storeId) ||
    nextStatus !== normalize(initialFilters.status) ||
    nextDirection !== normalize(initialFilters.direction) ||
    nextProvider !== normalize(initialFilters.provider) ||
    nextDateFrom !== normalize(initialFilters.dateFrom) ||
    nextDateTo !== normalize(initialFilters.dateTo);

  const rangeTriggerLabel =
    nextDateFrom || nextDateTo
      ? `${nextDateFrom || "…"} → ${nextDateTo || "…"}`
      : t(keys.integrationLogs.filterDateAll);

  const storeSelectOptions = useMemo(
    () => [
      { label: t(keys.integrationLogs.filterAllStores), value: "" },
      // Seller name rides along so two stores with the same name stay
      // distinguishable in the dropdown.
      ...storeOptions.map((store) => ({
        label: `${store.storeName} — ${store.sellerName}`,
        value: store.id,
      })),
    ],
    [storeOptions, t],
  );

  const statusOptions = useMemo(
    () => [
      { label: t(keys.integrationLogs.filterAllStatuses), value: "" },
      ...STATUS_VALUES.map((value) => ({
        label: t(STATUS_LABEL_KEY[value]),
        value,
      })),
    ],
    [t],
  );

  const directionOptions = useMemo(
    () => [
      { label: t(keys.integrationLogs.filterAllDirections), value: "" },
      ...DIRECTION_VALUES.map((value) => ({
        label: t(DIRECTION_LABEL_KEY[value]),
        value,
      })),
    ],
    [t],
  );

  // Filters apply themselves. One debounced effect covers every control, so
  // typing in the search box costs one request instead of one per keystroke,
  // and a dropdown change still feels instant. The guard is what stops this
  // from re-navigating to the URL we are already on (and looping): once the
  // navigation lands, the props it produced match the local state again.
  useEffect(() => {
    if (!hasPendingChanges) {
      return;
    }
    const timer = setTimeout(() => {
      navigate(
        `${basePath}?${buildIntegrationLogQuery({
          limit,
          offset: 0,
          filters: {
            search: nextSearch || undefined,
            storeId: nextStoreId || undefined,
            status: isStatus(nextStatus) ? nextStatus : undefined,
            direction: isDirection(nextDirection) ? nextDirection : undefined,
            provider: nextProvider || undefined,
            dateFrom: nextDateFrom || undefined,
            dateTo: nextDateTo || undefined,
          },
        })}`,
        // Replace, don't push: auto-applied filters would otherwise bury the
        // real previous page under a history entry per edit, and Back would
        // fight the local field state by restoring an older URL.
        { replace: true },
      );
    }, APPLY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [
    hasPendingChanges,
    nextSearch,
    nextStoreId,
    nextStatus,
    nextDirection,
    nextProvider,
    nextDateFrom,
    nextDateTo,
    basePath,
    limit,
    navigate,
  ]);

  // Resetting the fields is enough — the effect above navigates.
  function clearFilters() {
    setSearch("");
    setStoreId("");
    setStatus("");
    setDirection("");
    setProvider("");
    setDateFrom("");
    setDateTo("");
  }

  return (
    <form
      className="flex flex-wrap items-end gap-3 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3"
      onSubmit={(event) => event.preventDefault()}
    >
      <div className="w-64">
        <FormField label={t(keys.integrationLogs.filterSearchLabel)} name="search">
        <TextInput
          name="search"
          type="search"
          value={search}
          placeholder={t(keys.integrationLogs.filterSearchPlaceholder)}
          onChange={(event) => setSearch(event.target.value)}
        />
      </FormField>
      </div>

      <div className="w-56">
        <FormField label={t(keys.integrationLogs.filterStoreLabel)} name="storeId">
        <Combobox
          value={storeId}
          options={storeSelectOptions}
          onChange={(value) => setStoreId(value ?? "")}
          aria-label={t(keys.integrationLogs.filterStoreLabel)}
        />
      </FormField>
      </div>

      <div className="w-40">
        <FormField label={t(keys.integrationLogs.filterStatusLabel)} name="status">
        <Combobox
          value={status}
          options={statusOptions}
          onChange={(value) => setStatus(value ?? "")}
          aria-label={t(keys.integrationLogs.filterStatusLabel)}
        />
      </FormField>
      </div>

      <div className="w-40">
        <FormField label={t(keys.integrationLogs.filterDirectionLabel)} name="direction">
        <Combobox
          value={direction}
          options={directionOptions}
          onChange={(value) => setDirection(value ?? "")}
          aria-label={t(keys.integrationLogs.filterDirectionLabel)}
        />
      </FormField>
      </div>

      <div className="w-40">
        <FormField label={t(keys.integrationLogs.filterProviderLabel)} name="provider">
        <TextInput
          name="provider"
          value={provider}
          dir="ltr"
          placeholder={t(keys.integrationLogs.filterProviderPlaceholder)}
          onChange={(event) => setProvider(event.target.value)}
        />
      </FormField>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[12px] font-medium leading-tight text-[color:var(--label-ink)]">
          {t(keys.integrationLogs.filterDateRangeLabel)}
        </span>
        <DateRangePicker
          from={nextDateFrom || null}
          to={nextDateTo || null}
          todayIso={todayIso}
          locale={intlLocale(locale)}
          triggerLabel={rangeTriggerLabel}
          active={Boolean(nextDateFrom || nextDateTo)}
          applyLabel={t(keys.common.applyFilters)}
          clearLabel={t(keys.common.clearFilters)}
          onApply={(pickedFrom, pickedTo) => {
            setDateFrom(pickedFrom);
            setDateTo(pickedTo);
          }}
          onClear={() => {
            setDateFrom("");
            setDateTo("");
          }}
        />
      </div>

      {activeFilterCount > 0 ? (
        <Button
          type="button"
          onClick={clearFilters}
          variant="ghost"
          className="h-10 rounded-full px-3 text-sm font-semibold text-[color:var(--text-muted)]"
        >
          {t(keys.common.clearFilters)}
        </Button>
      ) : null}
    </form>
  );
}

